import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

console.log('🌐 API: Base URL configured as:', API_BASE_URL);
console.log('🌐 API: REACT_APP_API_URL env var:', process.env.REACT_APP_API_URL);

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false, // No cookies needed with JWT
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set JWT token for authenticated requests
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token); // Store for SSE authentication
    console.log('🌐 API: JWT token set for email-based user mapping');
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token'); // Remove from storage
    console.log('🌐 API: JWT token removed - will use default user ID 1');
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`🌐 API: Making ${config.method.toUpperCase()} request to ${config.url}`);
    const hasAuth = config.headers.Authorization ? '✅' : '❌';
    console.log(`🌐 API: JWT token for email mapping: ${hasAuth}`);
    if (!config.headers.Authorization) {
      console.log('🔐 API: No JWT token - backend will use default user ID 1');
    }
    return config;
  },
  (error) => {
    console.error('🌐 API: Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('🌐 API: Response received:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('🌐 API: API Error:', error.response?.data || error.message);
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Redirect to login or show auth modal
      window.dispatchEvent(new CustomEvent('auth-required'));
    }
    
    // Handle unauthorized access (403) - email not authorized
    if (error.response?.status === 403 && error.response?.data?.code === 'EMAIL_NOT_AUTHORIZED') {
      console.error('🚫 UNAUTHORIZED: Email not authorized for app access');
      
      // Clear auth token
      setAuthToken(null);
      
      // Trigger custom event for unauthorized email
      window.dispatchEvent(new CustomEvent('email-not-authorized', {
        detail: {
          message: error.response.data.message,
          email: error.response.data.email
        }
      }));
      
      // Mark error as unauthorized email for component handling
      error.isUnauthorizedEmail = true;
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authApi = {
  verifyJWT: () => api.get('/auth/verify'), // Verify current JWT token
  verifyUploadToken: (token) => api.post('/auth/verify-upload-token', { token }),
  getStatus: () => api.get('/auth/status'), // Returns user info if JWT is valid
  logout: () => api.post('/auth/logout'),
};

// Dashboard API (consolidated endpoint for better performance)
export const dashboardApi = {
  getData: (params = {}) => api.get('/dashboard/data', { params }),
};

// Users API
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (name) => api.post('/users', { name }),
  switch: (userId) => api.post('/users/switch', { userId }),
  update: (id, name) => api.put(`/users/${id}`, { name }),
  delete: (id) => api.delete(`/users/${id}`),
  updateProfile: (data) => api.put('/users/profile', data),
  syncProfile: (data) => api.post('/users/sync-profile', data),
};

// Files API
export const filesApi = {
  upload: (formData) => api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  process: (data) => api.post('/files/process', data),
  checkPassword: (data) => api.post('/files/check-password', data),
  getRecent: (limit = 10) => api.get('/files/recent', { params: { limit } }),
  delete: (id) => api.delete(`/files/${id}`),
};

// Jobs API
export const jobsApi = {
  getAll: () => api.get('/jobs'),
  getOne: (jobId) => api.get(`/jobs/${jobId}`),
  approve: (jobId, data) => api.post(`/jobs/${jobId}/approve`, data),
  reject: (jobId) => api.post(`/jobs/${jobId}/reject`),
  cancel: (jobId) => api.delete(`/jobs/${jobId}/cancel`),
  // Email jobs
  getEmailJobs: () => api.get('/jobs/email'),
  getEmailJob: (jobId) => api.get(`/jobs/email/${jobId}`),
  approveEmailJob: (jobId, data) => api.post(`/jobs/email/${jobId}/approve`, data),
  rejectEmailJob: (jobId) => api.post(`/jobs/email/${jobId}/reject`),
  cancelEmailJob: (jobId) => api.delete(`/jobs/email/${jobId}/cancel`),
};

// Transactions API
export const transactionsApi = {
  getAll: (params = {}) => api.get('/transactions', { params }),
  getTransactions: (params = {}) => api.get('/transactions', { params }).then(response => ({
    transactions: response.data || [],
    total: response.data?.length || 0
  })),
  create: (data) => api.post('/transactions', data), // Create single manual transaction
  save: (data) => api.post('/transactions/save', data), // Bulk save from file upload
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  bulkDelete: (transactionIds) => api.delete('/transactions/bulk/delete', { data: { transactionIds } }),
  bulkUpdate: (transactionIds, updates) => api.put('/transactions/bulk/update', { transactionIds, updates }),
  getDuplicates: () => api.get('/transactions/duplicates'),
  getSummary: (params = {}) => api.get('/transactions/summary', { params }),
  export: (data) => api.post('/transactions/export', data),
  splitTransaction: (id, data) => api.post(`/transactions/${id}/split`, data), // Split transaction into multiple
};

// Categories API
export const categoriesApi = {
  getAll: (params = {}) => api.get('/categories', { params }),
  // data: { name, kind?, groupName?, description? } (a plain string is the name)
  create: (data) => api.post('/categories', typeof data === 'string' ? { name: data } : data),
  update: (id, data) => api.put(`/categories/${id}`, typeof data === 'string' ? { name: data } : data),
  delete: (id) => api.delete(`/categories/${id}`),
  resetDefaults: () => api.post('/categories/reset-defaults'),
  getStats: () => api.get('/categories/stats'),
  merge: (sourceCategories, destinationCategory) => api.post('/categories/merge', { sourceCategories, destinationCategory }),
};

// Analytics API
export const analyticsApi = {
  getChartData: (params = {}) => api.get('/analytics/chart-data', { params }),
  getCategoryTransactions: (params = {}) => api.get('/analytics/category-transactions', { params }),
  getInsights: (params = {}) => api.get('/analytics/insights', { params }),
  getDateRanges: () => api.get('/analytics/date-ranges'),
  getAccountIds: () => api.get('/analytics/account-ids'),
  
  // Comprehensive analytics method that combines all data
  getAnalytics: async (filters = {}) => {
    try {
      // Pass date filters directly as expected by backend
      const params = {};
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.category) params.category = filters.category;
      if (filters.accountType) params.account_type = filters.accountType;
      if (filters.accountId) params.account_id = filters.accountId;
      if (filters.tags) params.tags = filters.tags;
      
      // Fetch chart data, insights, and available categories/accounts
      const [chartResponse, insightsResponse, categoriesResponse] = await Promise.all([
        api.get('/analytics/chart-data', { params }),
        api.get('/analytics/insights', { params }),
        api.get('/categories')
      ]);
      
      const chartData = chartResponse.data;
      const insights = insightsResponse.data;
      
      // Transform data for React charts
      const data = {
        categoryPie: chartData.categorySpending?.map(item => ({
          name: item.category,
          value: Math.abs(item.amount),
          transactionCount: item.transactionCount
        })) || [],
        
        monthlyTrend: chartData.monthlyTrend?.map(item => ({
          month: item.month,
          amount: Math.abs(item.expenses),
          credits: Math.abs(item.credits)
        })) || [],
        
        // Income against everything that went out: spending, invested, big purchases
        incomeExpense: chartData.monthlyTrend?.map(item => ({
          month: item.month,
          income: Math.abs(item.credits),
          expenses: Math.abs(item.expenses),
          invested: Math.abs(item.invested || 0),
          bigPurchases: Math.abs(item.bigPurchases || 0)
        })) || [],
        
        categoryTrend: chartData.categoryTrend || [],
        topCategories: chartData.topCategories || [],
        
        dayOfWeek: chartData.dayOfWeek?.map(item => ({
          day: item.day,
          amount: Math.abs(item.amount),
          count: item.count,
          average: Math.abs(item.average)
        })) || [],
        
        bankAnalysis: chartData.bankAnalysis?.map(item => ({
          bank: item.bank,
          credits: item.credits || 0,
          expenses: Math.abs(item.expenses || 0),
          creditCount: item.creditCount || 0,
          expenseCount: item.expenseCount || 0
        })) || [],
        
        accountIdAnalysis: chartData.accountIdAnalysis?.map(item => ({
          accountId: item.accountId,
          credits: item.credits || 0,
          expenses: Math.abs(item.expenses || 0),
          creditCount: item.creditCount || 0,
          expenseCount: item.expenseCount || 0
        })) || []
      };
      
      // Calculate stats from the data
      const stats = {
        totalTransactions: chartData.categorySpending?.reduce((sum, item) => sum + item.transactionCount, 0) || 0,
        totalCredits: chartData.monthlyTrend?.reduce((sum, item) => sum + (item.credits || 0), 0) || 0,
        totalExpenses: chartData.monthlyTrend?.reduce((sum, item) => sum + (item.expenses || 0), 0) || 0,
        totalInvested: chartData.monthlyTrend?.reduce((sum, item) => sum + Math.abs(item.invested || 0), 0) || 0,
        totalBigPurchases: chartData.monthlyTrend?.reduce((sum, item) => sum + Math.abs(item.bigPurchases || 0), 0) || 0,
        // Money that left your bank accounts, by what it did
        bankMoneyOut: chartData.bankMoneyOut || null,
        netBalance: 0,
        avgTransaction: 0,
        dateRangeStart: filters.dateFrom || null,
        dateRangeEnd: filters.dateTo || null
      };
      
      // What's left of the income after spending, investing and big purchases
      stats.netBalance = stats.totalCredits - Math.abs(stats.totalExpenses) - stats.totalInvested - stats.totalBigPurchases;
      stats.avgTransaction = stats.totalTransactions > 0 ? Math.abs(stats.totalExpenses) / stats.totalTransactions : 0;
      
      // Get available categories and account IDs from transactions
      const categoriesList = categoriesResponse.data?.categories?.map(cat => cat.name) || [];
      
      // Fetch real account IDs from backend
      let accountIds = [];
      try {
        const accountIdsResponse = await api.get('/analytics/account-ids');
        accountIds = accountIdsResponse.data?.accountIds || [];
      } catch (error) {
        console.error('Error fetching account IDs:', error);
        accountIds = [];
      }
      
      return {
        data,
        stats,
        insights: insights.insights || [],
        categories: categoriesList,
        accountIds
      };
      
    } catch (error) {
      console.error('Error in getAnalytics:', error);
      throw error;
    }
  },
  
  exportData: async (params = {}) => {
    const { format, ...filterParams } = params;
    // Convert date filter format and handle arrays
    const filters = {};
    if (filterParams.dateFrom) filters.startDate = filterParams.dateFrom;
    if (filterParams.dateTo) filters.endDate = filterParams.dateTo;
    if (filterParams.category && filterParams.category.length > 0) filters.category = filterParams.category;
    if (filterParams.accountType) filters.accountType = filterParams.accountType;
    if (filterParams.accountId && filterParams.accountId.length > 0) filters.accountId = filterParams.accountId;
    if (filterParams.tags && filterParams.tags.length > 0) filters.tags = filterParams.tags;
    
    return api.post('/transactions/export', { format, filters }, { responseType: 'blob' });
  }
};

// Database API
export const databaseApi = {
  getStats: () => api.get('/database/stats'),
  reinitialize: (confirmation) => api.post('/database/reinitialize', { confirmation }),
  backup: () => api.get('/database/backup'),
  getConnectionStatus: () => api.get('/database/connection-status'),
  optimize: () => api.post('/database/optimize'),
  getOldDataStats: (months) => api.get('/database/old-data-stats', { params: { months } }),
  clearOldData: (months, confirmation) => api.post('/database/clear-old-data', { months, confirmation }),
};

// Payments API (Version 1)
export const paymentsApi = {
  getConfig: () => api.get('/payments/config'),
  createOrder: () => api.post('/payments/create-order'),
  verifyPayment: (paymentData) => api.post('/payments/verify', paymentData),
  getHistory: (params = {}) => api.get('/payments/history', { params }),
  getStatus: () => api.get('/payments/status'),
};

// Subscription API (Version 1)
export const subscriptionApi = {
  getStatus: () => api.get('/subscription/status'),
  getDetails: () => api.get('/payments/status'), // Alias
};

// Gmail API (Version 3)
export const gmailApi = {
  getStatus: () => api.get('/gmail/status'),
  getAuthUrl: () => api.get('/gmail/auth-url'),
  connect: (code) => api.post('/gmail/connect', { code }),
  disconnect: () => api.post('/gmail/disconnect'),
  updateAutomation: (settings) => api.post('/gmail/automation', settings),
  process: (data) => api.post('/gmail/process', data),
};

// Notifications API (Version 3)
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Budgets API
export const budgetsApi = {
  getAll: (type) => api.get('/budgets', { params: { type } }), // type: 'overall', 'category', or undefined for all
  getStatus: () => api.get('/budgets/status'),
  create: (budgetData) => api.post('/budgets', budgetData),
  update: (id, budgetData) => api.put(`/budgets/${id}`, budgetData),
  delete: (id) => api.delete(`/budgets/${id}`),
  // Analytics endpoints
  getComprehensiveAnalytics: () => api.get('/budgets/analytics/comprehensive'),
  getMonthlyAnalytics: () => api.get('/budgets/analytics/monthly'),
  getQuarterlyAnalytics: () => api.get('/budgets/analytics/quarterly'),
  getYearlyAnalytics: () => api.get('/budgets/analytics/yearly'),
  getTrends: () => api.get('/budgets/analytics/trends'),
};

// Tags API (Version 9)
export const tagsApi = {
  getAll: () => api.get('/tags'),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
};

// Friends API (Version 11)
export const friendsApi = {
  getAll: () => api.get('/friends'),
  add: (email) => api.post('/friends', { email }),
  remove: (id) => api.delete(`/friends/${id}`),
  search: (email) => api.get('/friends/search', { params: { email } })
};

// Friend Groups API (Version 11)
export const friendGroupsApi = {
  getAll: () => api.get('/friend-groups'),
  create: (data) => api.post('/friend-groups', data),
  update: (id, data) => api.put(`/friend-groups/${id}`, data),
  delete: (id) => api.delete(`/friend-groups/${id}`),
  addMembers: (id, emails) => api.post(`/friend-groups/${id}/members`, { emails }),
  removeMember: (id, email) => api.delete(`/friend-groups/${id}/members/${encodeURIComponent(email)}`)
};

// Shared Expenses API (Version 11)
export const sharedExpensesApi = {
  getAll: (filters) => api.get('/shared-expenses', { params: filters }),
  create: (data) => api.post('/shared-expenses', data),
  update: (id, data) => api.put(`/shared-expenses/${id}`, data),
  delete: (id) => api.delete(`/shared-expenses/${id}`),
  getSplits: (id) => api.get(`/shared-expenses/${id}/splits`),
  fromTransaction: (data) => api.post('/shared-expenses/from-transaction', data)
};

// Settlements API (Version 11)
export const settlementsApi = {
  getBalances: () => api.get('/settlements/balances'),
  settle: (friendEmail, amount, transactionId) => 
    api.post('/settlements/settle', { friendEmail, amount, transactionId }),
  simplify: (groupId) => api.post('/settlements/simplify', { groupId }),
  getHistory: () => api.get('/settlements/history'),
  getOverview: () => api.get('/settlements/overview'),
  getFriend: (email) => api.get(`/settlements/friends/${encodeURIComponent(email)}`),
  getGroup: (groupId) => api.get(`/settlements/groups/${groupId}`),
  settleInGroup: (groupId, data) => api.post(`/settlements/groups/${groupId}/settle`, data)
};

// Email Addresses API (Version 10 - Email Forwarding)
export const emailAddressApi = {
  get: () => api.get('/email-addresses/me'),
  generate: () => api.post('/email-addresses/generate'),
  delete: () => api.delete('/email-addresses/me')
};

// Received Emails API (Version 10 - Email Forwarding)
export const receivedEmailsApi = {
  getAll: (params = {}) => api.get('/received-emails', { params }),
  getById: (id) => api.get(`/received-emails/${id}`),
  process: (id, model) => api.post(`/received-emails/${id}/process`, { model }),
  processPending: (model) => api.post('/received-emails/process-pending', { model }),
  processSelected: (emailIds) => api.post('/received-emails/process-selected', { emailIds }),
  markProcessed: (emailIds) => api.post('/received-emails/mark-processed', { emailIds }),
  deleteSelected: (emailIds) => api.post('/received-emails/delete-selected', { emailIds }),
  sync: () => api.post('/received-emails/sync'),
  receiptDecision: (id, action) => api.post(`/received-emails/${id}/receipt-decision`, { action }),
  // Read a statement again with its PDF opened: { password, remember, label }
  unlockPdf: (id, data) => api.post(`/received-emails/${id}/unlock-pdf`, data),
  delete: (id) => api.delete(`/received-emails/${id}`),
  clearProcessed: () => api.delete('/received-emails/clear-processed')
};

// Health check
export const healthApi = {
  check: () => api.get('/health'),
};

// AI Agents API (Version 12)
export const agentsApi = {
  getAll: () => api.get('/agents'),
  getOne: (id) => api.get(`/agents/${id}`),
  create: (data) => api.post('/agents', data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  delete: (id) => api.delete(`/agents/${id}`),
  activate: (id) => api.post(`/agents/${id}/activate`),
  deactivate: (id) => api.post(`/agents/${id}/deactivate`),
  getActive: () => api.get('/agents/active/current'),
};

// Account Balances API (Version 12)
export const accountBalancesApi = {
  getAll: (params = {}) => api.get('/account-balances', { params }),
  getLatest: () => api.get('/account-balances/latest'),
  getTrend: (params = {}) => api.get('/account-balances/trend', { params }),
  create: (data) => api.post('/account-balances', data),
  delete: (id) => api.delete(`/account-balances/${id}`),
};

// Accounts (Version 15): bank accounts, cards, wallets, loans ...
export const accountsApi = {
  getAll: () => api.get('/accounts'),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  merge: (id, intoAccountId) => api.post(`/accounts/${id}/merge`, { intoAccountId }),
};

// Per-user settings (Version 15); models only for whitelisted users
export const userSettingsApi = {
  get: () => api.get('/user-settings'),
  update: (data) => api.put('/user-settings', data),
};

// Recurring payments and what's coming up (Version 15)
export const recurringApi = {
  getAll: () => api.get('/recurring'),
  getUpcoming: (days = 30) => api.get('/recurring/upcoming', { params: { days } }),
  detect: () => api.post('/recurring/detect'),
  confirm: (id) => api.post(`/recurring/${id}/confirm`),
  dismiss: (id) => api.post(`/recurring/${id}/dismiss`),
  end: (id) => api.post(`/recurring/${id}/end`),
};

// Bills read from email (Version 15)
export const billsApi = {
  getAll: (params = {}) => api.get('/bills', { params }),
  update: (id, data) => api.put(`/bills/${id}`, data),
};

// Everything from emails waiting for review, reviewed together
export const reviewApi = {
  get: () => api.get('/review'),
  // after: a review's asOf, to also count what arrived since it was loaded
  summary: (after) => api.get('/review/summary', { params: after ? { after } : {} }),
  // seenIds: every row the screen showed; transactions: the rows to save
  commit: (seenIds, transactions) => api.post('/review/commit', { seenIds, transactions }),
};

// Reconciliation: alerts, balance checks, statement checks, and the emails behind a transaction
export const checksApi = {
  get: () => api.get('/checks'),
  scan: () => api.post('/checks/scan'),
  dismiss: (id) => api.post(`/checks/alerts/${id}/dismiss`),
  evidence: (transactionId) => api.get(`/checks/evidence/${transactionId}`),
};

// Looking ahead: safe to spend, cards this cycle, friends' payments to settle
export const insightsApi = {
  forecast: () => api.get('/insights/forecast'),
  cards: () => api.get('/insights/cards'),
  splitSuggestions: () => api.get('/insights/split-suggestions'),
};

// Merchant names for payees ("PAYTM*SWIGGY BLR" is Swiggy)
export const merchantAliasesApi = {
  getAll: () => api.get('/merchant-aliases'),
  create: (data) => api.post('/merchant-aliases', data),
  delete: (id) => api.delete(`/merchant-aliases/${id}`),
};

// Saved passwords for statement PDFs (never sent back)
export const statementPasswordsApi = {
  getAll: () => api.get('/statement-passwords'),
  create: (data) => api.post('/statement-passwords', data),
  delete: (id) => api.delete(`/statement-passwords/${id}`),
};

// "Always do this for this payee" rules (Version 15)
export const payeeRulesApi = {
  getAll: () => api.get('/payee-rules'),
  delete: (id) => api.delete(`/payee-rules/${id}`),
};

// Helper function for handling API errors
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.response?.data?.error) {
    return error.response.data.error;
  } else if (error.message) {
    return error.message;
  } else {
    return defaultMessage;
  }
};

// Helper function for downloading files
export const downloadFile = (data, filename, mimeType = 'application/octet-stream') => {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default api;
